import { useRef } from 'react';
import { useSearchParams } from 'react-router';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { phase2Service } from '@/core/services/phase2';
import { practiceService } from '@/core/services/practice';
import { phase1Service } from '@/core/services/phase1';
import { formatNatural, fromNatural, toNatural } from '@/core/format/naturalBalance';
import { rememberChosenPeriod, resolveDefaultPeriodId } from '@/core/periodDefault';
import {
  EVIDENCE_FILE_ACCEPT,
  EVIDENCE_FILE_MAX_BYTES,
  WORKING_PAPER_NO_BALANCE,
  WORKING_PAPER_SYSTEM_GENERATED,
  type SaveWorkingPaperPayload,
  type WorkingPaperDetail,
  type WorkingPaperEvidenceRow,
  type WorkingPaperRow,
  type WorkingPaperSignOffAction,
  type WorkingPaperSuggestionSource,
} from '@/core/models/phase2.models';
import type { PeriodRow } from '@/core/models/practice.models';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { InputField, TextareaField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select } from '@/kit/Select';
import { openDialog } from '@/kit/dialog';
import { JournalDialog, type JournalDialogData } from './JournalDialog';
import { workingPaperGroups, workingPaperJournalPrefill, workingPaperStatusLabel } from './workingPaperJournal';
import { NO_PERIOD_PLACEHOLDER } from '@/core/format/periodLabel';
import { NoPeriodNote, fmt0, fmt2, numberFromInput, numberToInput, periodTo, useAlive, useClientInit, useLive } from './common';
import './TaxreturnWorkingpapers.scss';

/** An editable analysis row on the open lead schedule. */
interface EditableLine {
  /** React key only. */
  uid: number;
  lineType: string;
  narrative: string;
  currentAmount: number | null;
  priorAmount: number | null;
  /** The amounts as typed ("1." is not yet the number 1); unset until the user edits the box. */
  currentText?: string;
  priorText?: string;
}

let nextUid = 1;

const EVIDENCE_TYPES = ['Note', 'Document', 'ExternalLink'];

interface Model {
  clientId: string;
  periods: PeriodRow[];
  /** APT355: the client's periods have been read (so "no accounting period" is only said once it is known). */
  periodsLoaded: boolean;
  periodId: string;
  isLocked: boolean;
  schedules: WorkingPaperRow[];
  detail: WorkingPaperDetail | null;
  lines: EditableLine[];
  conclusion: string;
  evidenceTitle: string;
  evidenceDetail: string;
  evidenceType: string;
  /** ap5-a6 (item 130): where the document itself lives; maps to the API's storageRef. */
  evidenceRef: string;
  /**
   * prod-p3: the file being attached. Choosing a file uploads it; a Document with no file still records a plain
   * reference the way it always did.
   */
  evidenceFile: File | null;
  /**
   * APT338: the open schedule's rows came from the adjusted trial balance (nothing was saved for it), so the screen says so and the user reviews them
   * before saving.
   */
  fromTrialBalance: boolean;
  /** APT338 follow-up: where those rows came from (the adjusted trial balance, or the share capital register for Share capital). */
  suggestedFrom: WorkingPaperSuggestionSource | null;
  /** APT338 follow-up ("Explain difference"): the explanation box is open, its text, and whether the user has changed it (only a changed one is sent). */
  explaining: boolean;
  explanation: string;
  explanationEdited: boolean;
  /** A save, sign-off or evidence request is running (stops a second click). */
  saving: boolean;
  /** Add was pressed with no evidence title (draws the red outline). */
  evidenceTried: boolean;
}

/** APT338: the statuses the API works out; a schedule in either has no row on the server, so "Start" (which saves one) is not offered for it. */
const isDerivedStatus = (status: string) => status === WORKING_PAPER_NO_BALANCE || status === WORKING_PAPER_SYSTEM_GENERATED;

/**
 * Working papers (AP-007): template-based lead schedules that must agree to the adjusted trial balance before
 * sign-off, with evidence and preparer/reviewer sign-off. (Angular TaxreturnWorkingpapersComponent.)
 */
export function TaxreturnWorkingpapers() {
  const alive = useAlive();
  // The Validation Centre's fix links name the schedule to open (`?schedule=<type>`): that one is opened once the list has loaded.
  const [searchParams] = useSearchParams();
  const [m, set, ref] = useLive<Model>({
    clientId: '',
    periods: [],
    periodsLoaded: false,
    periodId: '',
    isLocked: false,
    schedules: [],
    detail: null,
    lines: [],
    conclusion: '',
    evidenceTitle: '',
    evidenceDetail: '',
    evidenceType: 'Note',
    evidenceRef: '',
    evidenceFile: null,
    fromTrialBalance: false,
    suggestedFrom: null,
    explaining: false,
    explanation: '',
    explanationEdited: false,
    saving: false,
    evidenceTried: false,
  });
  const fileInput = useRef<HTMLInputElement>(null);
  const rowsTable = useRef<HTMLTableElement>(null);

  const load = async () => {
    const periodId = ref.current.periodId;
    if (!periodId) {
      return;
    }
    try {
      const screen = await phase2Service.getWorkingPapers(periodId);
      if (!alive.current) return;
      set({ schedules: screen.schedules ?? [], isLocked: screen.isLocked });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  useClientInit(async (client) => {
    try {
      const result = await practiceService.getClientPeriods(client.id);
      if (!alive.current) return;
      const periods = result.periods ?? [];
      // The period the user last chose, else the latest with data, else the latest that has ended (see pickDefaultPeriodId).
      const periodId = (await resolveDefaultPeriodId(client.id, periods)) ?? '';
      set({ clientId: client.id, periods, periodsLoaded: true, periodId });
      if (periodId) {
        await load();
        // `wanted > 0` used to gate this, but FixedAssets is scheduleType 0 - the same value `Number(null)` gives
        // when there is no `?schedule=` param at all, so that check silently skipped opening Fixed assets from a
        // deep link. `searchParams.has` distinguishes "asked for schedule 0" from "no schedule was asked for".
        const wanted = Number(searchParams.get('schedule'));
        if (searchParams.has('schedule') && Number.isInteger(wanted) && wanted >= 0 && ref.current.schedules.some((row) => row.scheduleType === wanted)) {
          await open({ scheduleType: wanted });
        }
      }
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  });

  const bind = (detail: WorkingPaperDetail) => {
    // An unstarted schedule opens on the adjusted trial balance's own rows when the API has them for it (APT338: the Profit and Loss schedules and the
    // balance sheet ones other than Fixed assets, Loans and Taxation), otherwise on its template's standard analysis rows. Nothing is stored until Save.
    const suggested = detail.lines.length === 0 ? (detail.suggestedLines ?? []) : [];
    const lines: EditableLine[] =
      detail.lines.length > 0
        ? detail.lines.map((line) => ({
            uid: nextUid++,
            lineType: line.lineType,
            narrative: line.narrative ?? '',
            currentAmount: line.currentAmount,
            priorAmount: line.priorAmount,
          }))
        : suggested.length > 0
          ? suggested.map((line) => ({
              uid: nextUid++,
              lineType: line.lineType,
              narrative: line.narrative ?? '',
              currentAmount: line.currentAmount,
              priorAmount: line.priorAmount,
            }))
          : detail.templateLines
              .filter((line) => !line.isTotal)
              .map((line) => ({
                uid: nextUid++,
                lineType: line.lineType,
                narrative: line.narrative,
                currentAmount: null,
                priorAmount: null,
              }));
    set({
      detail,
      conclusion: detail.conclusion ?? '',
      lines,
      fromTrialBalance: suggested.length > 0,
      suggestedFrom: suggested.length > 0 ? (detail.suggestedSource ?? 'AdjustedTrialBalance') : null,
      explaining: false,
      explanation: detail.differenceExplanation ?? '',
      explanationEdited: false,
    });
  };

  /** Opens a lead schedule; an unstarted one is seeded from its template's standard rows. */
  const open = async (row: Pick<WorkingPaperRow, 'scheduleType'>) => {
    try {
      const detail = await phase2Service.getWorkingPaper(ref.current.periodId, row.scheduleType);
      if (alive.current) bind(detail);
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const buildSavePayload = (): SaveWorkingPaperPayload => {
    const s = ref.current;
    return {
      accountingPeriodId: s.periodId,
      scheduleType: s.detail!.scheduleType,
      templateCode: s.detail!.templateCode,
      conclusion: s.conclusion || null,
      // APT338 follow-up ("Explain difference"): only a changed explanation is sent (text stores it, empty clears it); otherwise the stored one is kept.
      ...(s.explanationEdited ? { differenceExplanation: s.explanation.trim() } : {}),
      lines: s.lines
        .filter((line) => line.lineType !== 'Total')
        .map((line, index) => ({
          lineType: line.lineType,
          narrative: line.narrative || null,
          currentAmount: line.currentAmount ?? 0,
          priorAmount: line.priorAmount ?? 0,
          sortOrder: (index + 1) * 10,
        })),
    };
  };

  /**
   * item 132: the only way a schedule could leave "NotStarted" used to be an implicit side effect of adding evidence
   * or signing off - both immediately blocked by the same "start it first" error. This creates it explicitly, from
   * the list, with no other precondition than the template existing.
   */
  const start = async (row: WorkingPaperRow) => {
    if (row.scheduleId) {
      void open(row);
      return;
    }

    try {
      const detail = await phase2Service.getWorkingPaper(ref.current.periodId, row.scheduleType);
      if (!alive.current) return;
      bind(detail);
      const saved = await phase2Service.saveWorkingPaper(buildSavePayload(), detail.eTag);
      if (!alive.current) return;
      bind(saved);
      notifySuccess(`${row.name} has been started.`);
      void load();
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const setLines = (lines: EditableLine[]) => set({ lines });

  const editLine = (uid: number, changes: Partial<EditableLine>) =>
    setLines(ref.current.lines.map((l) => (l.uid === uid ? { ...l, ...changes } : l)));

  const addLine = () =>
    setLines([
      ...ref.current.lines,
      { uid: nextUid++, lineType: 'Analysis', narrative: '', currentAmount: null, priorAmount: null },
    ]);

  const removeLine = (index: number) => setLines(ref.current.lines.filter((_, i) => i !== index));

  const scheduleTotal = m.lines
    .filter((line) => line.lineType !== 'Total')
    .reduce((total, line) => total + (line.currentAmount ?? 0), 0);

  /** The schedule must agree to the adjusted trial balance before it can be signed off. */
  const difference = Math.round((scheduleTotal - (m.detail?.reconciliation.atbTotal ?? 0)) * 100) / 100;
  const isReconciled = Math.abs(difference) < 0.005;

  /** True until the schedule exists server-side; both evidence and sign-off need it to exist. */
  const isScheduleStarted = !!m.detail?.scheduleId;

  /**
   * ap5-a6 (items 130/132): a schedule opened from the template has no server-side row until it is saved. Create the
   * schedule first, so evidence can hang off it and sign-off has something to sign.
   */
  const ensureScheduleStarted = async (): Promise<WorkingPaperDetail> => {
    const detail = ref.current.detail;
    if (!detail) {
      throw new Error('Open a lead schedule first.');
    }
    if (detail.scheduleId) {
      return detail;
    }
    const saved = await phase2Service.saveWorkingPaper(buildSavePayload(), detail.eTag);
    if (alive.current) bind(saved);
    return saved;
  };

  const save = async () => {
    const detail = ref.current.detail;
    if (!detail || ref.current.saving) {
      return;
    }

    set({ saving: true });
    try {
      const saved = await phase2Service.saveWorkingPaper(buildSavePayload(), detail.eTag);
      if (!alive.current) return;
      bind(saved);
      notifySuccess('The lead schedule has been saved.');
      void load();
    } catch (error) {
      if (alive.current) notifyError(error);
    } finally {
      if (alive.current) set({ saving: false });
    }
  };

  const signOff = async (action: WorkingPaperSignOffAction) => {
    if (!ref.current.detail || ref.current.saving) {
      return;
    }

    set({ saving: true });
    try {
      const started = await ensureScheduleStarted();
      const payload = { accountingPeriodId: ref.current.periodId, scheduleType: started.scheduleType, action };
      const detail =
        action === 'Review' || action === 'ClearReview'
          ? await phase2Service.reviewWorkingPaper(payload)
          : await phase2Service.signOffWorkingPaper(payload);
      if (!alive.current) return;
      bind(detail);
      notifySuccess('The lead schedule has been updated.');
      void load();
    } catch (error) {
      if (alive.current) notifyError(error);
    } finally {
      if (alive.current) set({ saving: false });
    }
  };

  /** prod-p3: picks up the chosen file and checks the size before anything is sent. */
  const onEvidenceFileChange = (input: HTMLInputElement) => {
    const file = input.files?.[0] ?? null;

    if (file && file.size > EVIDENCE_FILE_MAX_BYTES) {
      notifyError('The file must be 10 MB or smaller.');
      input.value = '';
      set({ evidenceFile: null });
      return;
    }

    set({ evidenceFile: file });
    // Naming the evidence after the file saves the preparer typing it twice.
    if (file && !ref.current.evidenceTitle.trim()) {
      set({ evidenceTitle: file.name });
    }
  };

  const downloadEvidenceFile = async (item: WorkingPaperEvidenceRow) => {
    try {
      await phase2Service.downloadEvidenceFile(ref.current.periodId, item.id);
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  /** Removes the attached file and keeps the note; deleting the whole item uses the bin icon. */
  const removeEvidenceFile = async (item: WorkingPaperEvidenceRow) => {
    const scheduleType = ref.current.detail?.scheduleType;
    try {
      await phase2Service.deleteEvidenceFile(ref.current.periodId, item.id);
      if (!alive.current) return;
      notifySuccess('The attached file has been removed.');
      if (scheduleType !== undefined) {
        void open({ scheduleType });
      }
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  /**
   * prod-p3: the schedule must exist before evidence can hang off it, exactly as for a note, so the upload goes
   * through the same ensureScheduleStarted step.
   */
  const uploadEvidence = async (file: File) => {
    const scheduleType = ref.current.detail!.scheduleType;
    const title = ref.current.evidenceTitle.trim() || file.name;
    const detail = ref.current.evidenceDetail.trim() || null;

    try {
      await ensureScheduleStarted();
      await phase2Service.uploadEvidenceFile(ref.current.periodId, scheduleType, file, title, detail, null);
      if (!alive.current) return;
      set({ evidenceTitle: '', evidenceDetail: '', evidenceRef: '', evidenceFile: null, evidenceTried: false });
      notifySuccess('The evidence has been attached.');
      void open({ scheduleType });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const addEvidence = async () => {
    const s = ref.current;
    if (s.saving) {
      return;
    }
    if (!s.detail || (!s.evidenceTitle.trim() && !s.evidenceFile)) {
      set({ evidenceTried: true });
      notifyError('Enter what the evidence is.');
      return;
    }

    set({ saving: true });
    try {
      await addEvidenceNow(s.detail.scheduleType, s.evidenceFile);
    } finally {
      if (alive.current) set({ saving: false });
    }
  };

  const addEvidenceNow = async (scheduleType: number, file: File | null) => {
    if (file) {
      await uploadEvidence(file);
      return;
    }

    try {
      await ensureScheduleStarted();
      const current = ref.current;
      await phase2Service.saveEvidence({
        accountingPeriodId: current.periodId,
        scheduleType,
        id: null,
        evidenceType: current.evidenceType,
        title: current.evidenceTitle.trim(),
        detail: current.evidenceDetail.trim() || null,
        // ap5-a6: where the document itself lives (a DMS link or file reference). The column has always existed
        // server-side; the screen never offered a way to fill it in.
        storageRef: current.evidenceRef.trim() || null,
        amount: null,
      });
      if (!alive.current) return;
      set({ evidenceTitle: '', evidenceDetail: '', evidenceRef: '', evidenceTried: false });
      notifySuccess('The evidence has been recorded.');
      void open({ scheduleType });
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const deleteEvidence = async (id: string) => {
    try {
      await phase2Service.deleteEvidence(ref.current.periodId, id);
      if (!alive.current) return;
      notifySuccess('The evidence has been removed.');
      const detail = ref.current.detail;
      if (detail) {
        void open({ scheduleType: detail.scheduleType });
      }
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const { detail, isLocked } = m;

  /**
   * APT349: the natural side of the open schedule's reporting codes (1 debit, -1 credit; null when they mix both sides). The rows are held, saved and compared with
   * the trial balance in the STORED signs (debits positive, credits negative) exactly as before; only what the user sees and types follows the code's natural side -
   * turnover is typed 49,000, not -49,000 - and is converted to the stored sign as it is entered (and back when it is shown).
   */
  const normalSign = detail?.normalSign ?? null;
  const shownAmount = (stored: number | null) => numberToInput(stored === null ? null : toNatural(stored, normalSign) + 0);
  const storedAmount = (typed: string) => {
    const n = numberFromInput(typed);
    // A lone "-" or "." is not a number yet: it counts as empty until more is typed.
    return n === null || !Number.isFinite(n) ? null : fromNatural(n, normalSign) + 0;
  };

  /**
   * APT338 (the client: "provide an option to create journals as per the working papers"): a difference that is genuine means the adjusted trial balance is
   * wrong, so the working paper starts the Adjustment journal that fixes it - dated the last day of the period, with the difference already in its lines -
   * instead of sending the user to Journals and back. The figures are refreshed when it is saved, without losing the rows being edited.
   */
  const journalPrefill = detail && !isLocked ? workingPaperJournalPrefill(detail, difference) : null;

  /** APT338 follow-up ("Correct Schedule"): take the user to the rows of the schedule. Nothing is written to the adjusted trial balance by changing them. */
  const correctSchedule = () => {
    const first = rowsTable.current?.querySelector<HTMLInputElement>('input.wp-amount');
    first?.focus();
    first?.scrollIntoView?.({ block: 'center' });
  };

  /** APT338 follow-up ("Explain Difference"): a required, short explanation kept with the schedule; saved with it (the rows as shown). */
  const saveExplanation = async () => {
    const text = ref.current.explanation.trim();
    if (!text) {
      notifyError('Enter an explanation of the difference.');
      return;
    }
    if (text.length > 1000) {
      notifyError('The explanation can be at most 1000 characters.');
      return;
    }
    await save();
  };

  const createJournal = async () => {
    const current = ref.current.detail;
    const prefill = current ? workingPaperJournalPrefill(current, difference) : null;
    if (!current || !prefill) {
      return;
    }

    try {
      const types = (await phase1Service.getJournalTypes()).filter((t) => !t.isGroupOnly);
      const data: JournalDialogData = {
        periodId: ref.current.periodId,
        clientId: ref.current.clientId,
        journalId: null,
        readOnly: false,
        types,
        defaultDate: prefill.journalDate,
        prefill,
        // APT338 follow-up 2: the journal made from this working paper carries an explanation and evidence, kept on the schedule too.
        evidence: {
          scheduleType: current.scheduleType,
          ensureSchedule: async () => {
            await ensureScheduleStarted();
          },
        },
      };
      const saved = await openDialog(JournalDialog, {
        disableClose: true,
        panelClass: 'cst-invite',
        width: '1080px',
        maxWidth: '94vw',
        data,
      }).afterClosed();
      if (!saved || !alive.current) {
        return;
      }

      // Back on the same working paper: the figures it agrees to are refreshed; the rows being edited stay as they are.
      const fresh = await phase2Service.getWorkingPaper(ref.current.periodId, current.scheduleType);
      if (alive.current) set({ detail: fresh });
      void load();
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  const columns: DataTableColumn<WorkingPaperRow>[] = [
    {
      id: 'name',
      header: 'Lead schedule',
      cell: (element) => (
        <>
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              void open(element);
            }}
          >
            {element.name}
          </a>
          {element.isStale && <span> &nbsp;(sign-off out of date)</span>}
        </>
      ),
    },
    // APT349 (the client: balances follow the natural Debit/Credit side of the reporting code): the stored totals (debits positive, credits negative) are shown as a
    // person reads them - plain on the code's own side, in brackets on the other - so Turnover reads 49,000.00, not -49,000.00. The difference is schedule - ATB in
    // the same presentation, so a schedule below the trial balance is in brackets whichever side the code lies on.
    { id: 'atb', header: 'Adjusted trial balance', cell: (element) => formatNatural(element.atbTotal, element.normalSign) },
    { id: 'schedule', header: 'Schedule', cell: (element) => formatNatural(element.scheduleTotal, element.normalSign) },
    {
      id: 'difference',
      header: 'Difference',
      cell: (element) => (
        <>
          {formatNatural(element.difference, element.normalSign)}
          {!element.isReconciled && <span> - must be cleared before sign-off</span>}
          {/* APT338 follow-up: an explanation does not clear it - it only says why, for the reviewer. */}
          {!element.isReconciled && element.hasExplanation && <span> (explained)</span>}
        </>
      ),
    },
    // APT338: NoBalance and SystemGenerated are the API's own statuses (worked out when read, never stored), given plain words here.
    { id: 'status', header: 'Status', cell: (element) => workingPaperStatusLabel(element.status) },
    {
      id: 'evidence',
      header: 'Evidence',
      cell: (element) => element.evidenceCount,
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (element) => (
        <div className="d-flex align-items-center grid-actions">
          {/* item 132: a schedule nobody has touched yet can be started directly from the list, rather than only
              as a side effect of trying to add evidence or sign off. */}
          {!element.scheduleId && !isDerivedStatus(element.status) && (
            <Button title="Start" onClick={() => void start(element)}>
              <Icon name="play_circle_outline" outlined />
            </Button>
          )}
          <Button title="Open" onClick={() => void open(element)}>
            <Icon name="edit" outlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <SideMenuPage menu="task">
      <div className="content-body taxreturn-workingpapers">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            {/* The Adjustments & Reports bar above already shows "Working Papers" as the active tab - this repeated
                the same word as a page heading right underneath it; kept visually hidden as the page's accessible name. */}
            <h2 className="cdk-visually-hidden">Working Papers</h2>
          </div>
          <div className="content-header-right mb-1">
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              placeholder={NO_PERIOD_PLACEHOLDER}
              items={m.periods}
              bindLabel="periodFrom"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={m.periodId || null}
              onChange={(value) => {
                rememberChosenPeriod(value as string);
                set({ periodId: value as string, detail: null });
                void load();
              }}
              renderLabel={periodTo}
              renderOption={periodTo}
            />
          </div>
        </div>
        <NoPeriodNote show={m.periodsLoaded && m.periods.length === 0} />

        {isLocked && <p className="mb-1">This period is finalised, so its working papers are read only.</p>}

        {/* APT338 follow-up (the client, 28 Oct 2026 10:36): "organise the screen rather than showing one long flat list" - Assets, Liabilities, Equity, Profit & Loss,
            in that order, the schedules in the client's order inside each (the API sends them so; the starred ones only when the trial balance has values). */}
        {!detail &&
          workingPaperGroups(m.schedules).map((group) => (
            <div className="mat-table-wrapper wp-group" key={group.name || 'all'}>
              {group.name && <h3 className="wp-group-heading">{group.name}</h3>}
              <DataTable columns={columns} rows={group.rows} rowKey={(row) => row.scheduleType} />
            </div>
          ))}

        {detail && (
          <div className="sidenav-details">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2>{detail.name}</h2>
                <Button className="close-btn" title="Close" aria-label="Close" onClick={() => set({ detail: null })}>
                  <Icon name="close" />
                </Button>
              </div>
            </div>
            <div className="sidenav-body pt-1">
              {detail.helpText && <p>{detail.helpText}</p>}
              <div className="wp-recon-row">
                <span className={`wp-status-badge ${isReconciled ? 'ok' : 'warn'}`}>
                  {isReconciled ? '✓ Reconciled' : 'Not reconciled'}
                </span>
                <div className="wp-recon-figures">
                  <span>
                    Adjusted trial balance <b>{formatNatural(detail.reconciliation.atbTotal, normalSign)}</b>
                  </span>
                  <span>
                    Schedule <b>{formatNatural(scheduleTotal, normalSign)}</b>
                  </span>
                  <span>
                    Difference <b>{formatNatural(difference, normalSign)}</b>
                  </span>
                </div>
              </div>
              {!isReconciled && <p className="text-gray font-size-12 mt-0">(the schedule must agree before it can be signed off)</p>}
              {detail.isStale && <p>The figures changed after this schedule was signed off; review it again.</p>}
              {/* APT338 follow-up 2 (the client, 28 Oct 2026 21:35: "retain the full nominal value of the shares and separately identify the amount paid up and amount unpaid"). */}
              {detail.shareCapital && (
                <table className="wp-100 share-capital-summary" aria-label="Share capital: nominal value, paid up and unpaid">
                  <tbody>
                    <tr>
                      <th></th>
                      <th>This period</th>
                      <th>Prior period</th>
                    </tr>
                    <tr>
                      <td>Nominal value of the shares</td>
                      <td>{fmt2(detail.shareCapital.nominalValue)}</td>
                      <td>{fmt2(detail.shareCapital.priorNominalValue)}</td>
                    </tr>
                    <tr>
                      <td>Amount paid up</td>
                      <td>{fmt2(detail.shareCapital.amountPaidUp)}</td>
                      <td>{fmt2(detail.shareCapital.priorAmountPaidUp)}</td>
                    </tr>
                    <tr>
                      <td>Amount unpaid (called up share capital not paid)</td>
                      <td>{fmt2(detail.shareCapital.amountUnpaid)}</td>
                      <td>{fmt2(detail.shareCapital.priorAmountUnpaid)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
              {detail.shareCapital && !detail.shareCapital.unpaidRecorded && (
                <p className="text-gray" role="note">
                  A class is marked partly paid, but the amount paid on each share is not recorded, so the unpaid amount is not known. Enter it in the Share Capital
                  section.
                </p>
              )}
              {/* APT338: the rows below come from the adjusted trial balance; the user checks them, changes anything that is wrong and saves. */}
              {m.fromTrialBalance && m.suggestedFrom !== 'ShareCapitalRegister' && (
                <p className="text-gray" role="note">
                  These rows come from the adjusted trial balance. Check them, change anything that is wrong, add your supporting details, then save. You still
                  review and sign off yourself.
                </p>
              )}
              {/* APT338 follow-up: Share capital is fed by the Share Capital section, as Fixed assets are by their register, and compared with the trial balance. */}
              {m.fromTrialBalance && m.suggestedFrom === 'ShareCapitalRegister' && (
                <p className="text-gray" role="note">
                  These rows come from the Share Capital section of the company information (the share capital register). Check them against the adjusted trial
                  balance, then save.
                </p>
              )}
              {detail.status === WORKING_PAPER_NO_BALANCE && (
                <p className="text-gray" role="note">
                  The trial balance, the journals and the adjusted trial balance have nothing for this schedule, so no work is required and it does not hold up the
                  accounts. If a balance or an entry is added, it becomes an active schedule again.
                </p>
              )}
              {/* APT338 follow-up ("Explain difference"): the explanation kept with the schedule. */}
              {detail.differenceExplanation && !m.explaining && (
                <p className="text-gray" role="note">
                  Explanation of the difference: {detail.differenceExplanation}
                  {detail.differenceExplainedAtUtc && <span> ({detail.differenceExplainedAtUtc.slice(0, 10)})</span>}
                </p>
              )}

              <table className="wp-100 wp-lines-table" ref={rowsTable}>
                <tbody>
                  <tr>
                    <th>Narrative</th>
                    <th className="wp-amount-col">This period</th>
                    <th className="wp-amount-col">Prior period</th>
                    <th className="wp-action-col">Action</th>
                  </tr>
                  {m.lines.map((line, i) => (
                    <tr key={line.uid}>
                      <td>
                        <InputField
                          fieldClassName="wp-100 wp-narrative-field"
                          maxLength={500}
                          value={line.narrative}
                          disabled={isLocked}
                          onChange={(event) => editLine(line.uid, { narrative: event.target.value })}
                        />
                      </td>
                      <td className="wp-amount-col">
                        <InputField
                          fieldClassName="wp-100"
                          className="wp-amount"
                          charMode="number"
                          allowDecimal
                          allowNegative
                          maxIntDigits={15}
                          maxDecimals={4}
                          value={line.currentText ?? shownAmount(line.currentAmount)}
                          disabled={isLocked}
                          onChange={(event) =>
                            editLine(line.uid, {
                              currentAmount: storedAmount(event.target.value),
                              currentText: event.target.value,
                            })
                          }
                        />
                      </td>
                      <td className="wp-amount-col">
                        <InputField
                          fieldClassName="wp-100"
                          className="wp-amount"
                          charMode="number"
                          allowDecimal
                          allowNegative
                          maxIntDigits={15}
                          maxDecimals={4}
                          value={line.priorText ?? shownAmount(line.priorAmount)}
                          disabled={isLocked}
                          onChange={(event) =>
                            editLine(line.uid, {
                              priorAmount: storedAmount(event.target.value),
                              priorText: event.target.value,
                            })
                          }
                        />
                      </td>
                      <td className="wp-action-col">
                        <Button title="Remove" onClick={() => removeLine(i)} disabled={isLocked}>
                          <Icon name="delete_outline" outlined />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Button onClick={addLine} disabled={isLocked}>
                <Icon name="add" />
                <span>Row</span>
              </Button>

              <div className="form-field textarea">
                <p className="mb-1">Conclusion</p>
                <TextareaField
                  fieldClassName="wp-100"
                  rows={3}
                  maxLength={2000}
                  value={m.conclusion}
                  disabled={isLocked}
                  onChange={(event) => set({ conclusion: event.target.value })}
                />
              </div>

              {/* APT338 follow-up (the client, 28 Oct 2026 10:36): "Where a difference exists, provide options such as Create Journal | Correct Schedule | Explain
                  Difference." Sign-off stays blocked until the difference is cleared; changes to the schedule never write to the adjusted trial balance. */}
              {!isReconciled && !isLocked && (
                <div className="mt-1" role="group" aria-label="What to do about the difference">
                  <p className="mb-1">
                    The schedule and the adjusted trial balance differ by {formatNatural(difference, normalSign)}. Correct the schedule if it is wrong, or create an adjustment journal if the
                    schedule is right - the journal takes your explanation and evidence. An explanation on its own does not clear the difference.
                  </p>
                  <div className="d-flex align-items-center flex-wrap">
                    {journalPrefill && (
                      <Button className="action-button secondary-button mr-1" onClick={() => void createJournal()}>
                        Create adjustment journal
                      </Button>
                    )}
                    <Button className="action-button secondary-button mr-1" onClick={correctSchedule}>
                      Correct schedule
                    </Button>
                    <Button className="action-button secondary-button" onClick={() => set({ explaining: !m.explaining })}>
                      Explain difference
                    </Button>
                  </div>
                  {m.explaining && (
                    <div className="form-field textarea mt-1">
                      <p className="mb-1">Explain the difference (required)</p>
                      <TextareaField
                        fieldClassName="wp-100"
                        rows={3}
                        maxLength={1000}
                        value={m.explanation}
                        onChange={(event) => set({ explanation: event.target.value, explanationEdited: true })}
                      />
                      <p className="text-gray font-size-12">
                        The explanation is kept with the schedule for the reviewer. It does not allow sign-off: the difference must still be cleared, by correcting the schedule or by an
                        adjustment journal (which takes its own explanation and evidence).
                      </p>
                      <Button className="action-button primary-button" busy={m.saving} onClick={() => void saveExplanation()}>
                        Save explanation
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {/* ap5-a6 (item 132): the status is derived from sign-off, and sign-off is blocked until the schedule
                  agrees to the adjusted trial balance. Say so, rather than leaving the buttons greyed out. */}
              <div className="text-gray font-size-12 mt-5">
                Status: <span className="fw-bold">{workingPaperStatusLabel(detail.status)}</span>
                {!isReconciled && (
                  <span>
                    &nbsp;&middot;&nbsp;Sign-off is available once the schedule agrees to the adjusted trial balance
                    (difference {formatNatural(difference, normalSign)}).
                  </span>
                )}
              </div>

              <h3 className="mt-1">
                Evidence <span className="text-gray font-size-14">({detail.evidence.length})</span>
              </h3>
              {/* ap5-a6 (item 131): the list had no header and no scroll area of its own. */}
              <div className="evidence-list">
                <table className="wp-100">
                  <tbody>
                    <tr>
                      <th>Evidence</th>
                      <th>Detail</th>
                      <th>Type</th>
                      <th>Reference</th>
                      {/* prod-p3: the attached file itself, not just a reference to one. */}
                      <th>File</th>
                      <th>Action</th>
                    </tr>
                    {detail.evidence.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-gray">
                          No evidence recorded against this schedule yet.
                        </td>
                      </tr>
                    )}
                    {detail.evidence.map((item) => (
                      <tr key={item.id}>
                        <td>
                          {item.title}
                          {item.journalNo ? <span className="text-gray font-size-12"> (journal {item.journalNo})</span> : null}
                        </td>
                        <td>{item.detail}</td>
                        <td>{item.evidenceType}</td>
                        <td className="text-gray">{item.storageRef || '-'}</td>
                        <td>
                          {item.hasFile ? (
                            <>
                              <a
                                className="link"
                                onClick={() => void downloadEvidenceFile(item)}
                                title={item.fileName ?? undefined}
                              >
                                {item.fileName}
                              </a>
                              <span className="text-gray font-size-12">
                                &nbsp;({fmt0((item.fileSizeBytes || 0) / 1024)} KB)
                              </span>
                            </>
                          ) : (
                            <span className="text-gray">-</span>
                          )}
                        </td>
                        <td>
                          {item.hasFile && (
                            <Button
                              title="Remove the attached file"
                              onClick={() => void removeEvidenceFile(item)}
                              disabled={isLocked}
                            >
                              <Icon name="attachment" outlined />
                            </Button>
                          )}
                          <Button title="Delete" onClick={() => void deleteEvidence(item.id)} disabled={isLocked}>
                            <Icon name="delete_outline" outlined />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex align-items-center flex-wrap">
                <InputField
                  fieldClassName="mr-1"
                  placeholder="Evidence"
                  maxLength={255}
                  invalid={m.evidenceTried && !m.evidenceTitle.trim() && !m.evidenceFile}
                  value={m.evidenceTitle}
                  disabled={isLocked}
                  onChange={(event) => set({ evidenceTitle: event.target.value })}
                />
                <span className="mr-1">
                  <RequiredMark />
                </span>
                <InputField
                  fieldClassName="mr-1"
                  placeholder="Detail"
                  maxLength={1000}
                  value={m.evidenceDetail}
                  disabled={isLocked}
                  onChange={(event) => set({ evidenceDetail: event.target.value })}
                />
                <Select
                  className="w-150 mr-1"
                  items={EVIDENCE_TYPES}
                  clearable={false}
                  searchable={false}
                  value={m.evidenceType}
                  disabled={isLocked}
                  onChange={(value) => set({ evidenceType: value as string })}
                />
                {/* ap5-a6 (item 130): a Document/link reference; the API has always stored it. */}
                {m.evidenceType !== 'Note' && (
                  <InputField
                    fieldClassName="mr-1"
                    placeholder="Link or file reference"
                    maxLength={500}
                    value={m.evidenceRef}
                    disabled={isLocked}
                    onChange={(event) => set({ evidenceRef: event.target.value })}
                  />
                )}
                {/* prod-p3: attach the document itself. The API caps it at 10 MB and checks the bytes, not the
                    name, before it is stored. */}
                <input
                  ref={fileInput}
                  type="file"
                  className="d-none"
                  accept={EVIDENCE_FILE_ACCEPT}
                  disabled={isLocked}
                  onChange={(event) => onEvidenceFileChange(event.target)}
                />
                <Button
                  className="action-button secondary-button mr-1"
                  onClick={() => fileInput.current?.click()}
                  disabled={isLocked}
                >
                  <Icon name="attach_file" outlined />
                  {m.evidenceFile ? m.evidenceFile.name : 'Attach file'}
                </Button>
                <Button
                  className="action-button primary-button"
                  busy={m.saving}
                  onClick={() => {
                    void addEvidence();
                    if (fileInput.current) fileInput.current.value = '';
                  }}
                  disabled={isLocked}
                >
                  Add
                </Button>
              </div>
              <div className="text-gray font-size-12 mt-5">
                Attach a PDF, image, CSV, Excel or Word file up to 10 MB - for example the bank statement the schedule
                was reconciled to.
              </div>
              {!isScheduleStarted && (
                <div className="text-gray font-size-12 mt-5">
                  Adding evidence or signing off will start this lead schedule first.
                </div>
              )}
            </div>
            {/* The four actions below used to scroll away with the page content; pinned to match the app's
                standard persistent sticky-bottom action bar (reuses the same .kit-page-header-footer look). */}
            <div className="kit-page-header-footer">
              <Button className="action-button primary-button" busy={m.saving} onClick={() => void save()} disabled={isLocked}>
                Save
              </Button>
              <Button
                className="action-button secondary-button"
                busy={m.saving}
                onClick={() => void signOff('Prepare')}
                disabled={isLocked || !isReconciled}
              >
                Sign off as preparer
              </Button>
              <Button
                className="action-button secondary-button"
                busy={m.saving}
                onClick={() => void signOff('Review')}
                disabled={isLocked || !isReconciled}
              >
                Sign off as reviewer
              </Button>
              <Button
                className="action-button secondary-button"
                busy={m.saving}
                onClick={() => void signOff('ClearReview')}
                disabled={isLocked || !detail.reviewedAtUtc}
              >
                Clear review
              </Button>
            </div>
          </div>
        )}
      </div>
    </SideMenuPage>
  );
}
